import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useEvents } from "../../hooks/events/useEvents";
import Timeslots from "./Timeslots";
import moment from "moment";
import VideoViewing from "./VideoViewing";
import theme from "../../styles/styleTheme";
import { useStages } from "../../hooks/tenants/useStages";
import { useTranslation } from "react-i18next";
import { useChildEvents } from "../../hooks/events/useChildEvents";
import { useUserAuth } from "../../context/UserAuthContext";

type BookViewingProps = {
  property: any;
};

const BookViewing = ({ property }: BookViewingProps) => {
  const [openAlert, setOpenAlert] = useState(false);
  const [dialogText, setDialogText] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogCallback, setDialogCallback] = useState<any>(null);
  const [isLoadingSubscribe, setIsLoadingSubscribe] = useState(false);
  const [attendingToEventId, setAttendingToEventId] = useState<any>(null);
  const [allEventsFullAndIsNotAttending, setAllEventsFullAndIsNotAttending] =
    useState(false);

  const { t } = useTranslation();
  const { user } = useUserAuth();

  const { getDataById: getEventsByPropertyId, data: events } = useEvents();
  const { getData: getStagesdata } = useStages();
  const { getDataById: getTimeslots } = useChildEvents();

  useEffect(() => {
    if (property != null) {
      getEventsByPropertyId(property.id);
      getStagesdata();
    }
  }, [property]);

  useEffect(() => {
    const sortOutEvents = async () => {
      await events.forEach(async (event: any) => {
        await getTimeslots(event.id).then((timeslots: any) => {
          const isAttending = timeslots.some((timeslot: any) =>
            user.tenant.attendingEvents.includes(timeslot.id),
          );
          if (isAttending) {
            setAttendingToEventId(timeslots[0].parentId);
          }
        });
      });
    };

    if (events?.length > 0) {
      sortOutEvents();
    }
  }, [events]);

  useEffect(() => {
    if (events?.length > 0) {
      const isAttendingEvent = attendingToEventId !== null;
      setAllEventsFullAndIsNotAttending(
        events.every(
          (event: any) => event.currentCandidates >= event.maxCandidates,
        ) && !isAttendingEvent,
      );
    }
  }, [attendingToEventId, events]);

  const openDialog = (title: string, text: string, callback: any) => {
    setDialogTitle(title);
    setDialogText(text);
    setOpenAlert(true);
    if (callback) {
      setDialogCallback(() => callback);
    }
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const capitalize = (s: string) => {
    if (typeof s !== "string") return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  return (
    <>
      <Typography>{t("book-viewing.title")}</Typography>
      {!isLoadingSubscribe && (
        <Box display={"flex"} flexDirection={"column"}>
          {events.map((event: any, index: number) => (
            <>
              {(event.currentCandidates < event.maxCandidates ||
                attendingToEventId === event.id) && (
                <div key={index}>
                  <Divider
                    sx={{
                      marginBottom: 1,
                      marginTop: 2,
                      "&::before, &::after": {
                        borderColor: "#b8b8b8",
                      },
                    }}
                  >
                    <Typography variant="body2" key={index}>
                      {capitalize(
                        moment(event.startDateTime).format("dddd, D-MM-YYYY"),
                      )}
                    </Typography>
                  </Divider>
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    flexDirection={"column"}
                    gap={1}
                    marginTop={1}
                    key={event.id}
                  >
                    <Timeslots
                      event={event}
                      openDialog={openDialog}
                      isLoadingSubscribe={isLoadingSubscribe}
                      setIsLoadingSubscribe={setIsLoadingSubscribe}
                    />
                  </Box>
                </div>
              )}
            </>
          ))}
          {allEventsFullAndIsNotAttending && (
            <>
              <Box
                padding="1rem"
                textAlign={"center"}
                style={{
                  backgroundColor: "#eee",
                  color: "#fff",
                  borderRadius: "5px",
                  marginTop: "1rem",
                }}
              >
                <Typography color={"#686868"}>
                  {t("viewing.all-events-booked")}
                </Typography>
              </Box>
            </>
          )}
          <VideoViewing
            attendingToEventId={attendingToEventId}
            propertyId={property?.id}
          />
        </Box>
      )}
      {isLoadingSubscribe && (
        <Box
          padding="3rem"
          textAlign={"center"}
          style={{
            backgroundColor: theme.palette.primary.light,
            color: "#fff",
            borderRadius: "1rem",
            marginTop: "1rem",
          }}
        >
          <Typography
            style={{
              marginBottom: "3rem",
              color: theme.palette.primary.main,
            }}
          >
            {t("book-viewing.loading")}
          </Typography>
          <CircularProgress />
        </Box>
      )}
      <Dialog open={openAlert} onClose={handleCloseAlert}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAlert}>{t("No")}</Button>
          <Button
            onClick={() => {
              if (dialogCallback) {
                dialogCallback();
              }
              handleCloseAlert();
            }}
            autoFocus
          >
            {t("Yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookViewing;
